import { useCallback, useMemo, useRef, useState } from 'react';
import { env, isConfigured } from '../../config/env';
import { useLocalDateKey, readJson, removeJson, writeJson } from '../storage';
import { usePolling } from '../poll';
import { loadCalendar } from './calendarApi';
import { groupEventsByDay, windowDateKeys, type CalendarEvent, type DayGroup } from './events';
import {
  isTokenLive,
  requestAccessToken,
  revokeAccessToken,
  type AccessToken,
} from './gis';

/**
 * The Calendar panel's data hook. Mounted exactly once (unlike useWeather),
 * so ordinary component state is the right tool here — but the network call
 * still carries a module-level in-flight guard, because StrictMode
 * double-invokes effects in dev and a doubled OAuth-authenticated request is
 * the kind of thing that quietly eats a quota.
 *
 * TOKEN PERSISTENCE — a deliberate, documented departure from DECISIONS.md,
 * which says tokens "are not persisted". They are persisted here, under the
 * `calendar` storage area the Mayor's registry in src/lib/storage.ts already
 * reserves for exactly this ("calendar  weather-cal  OAuth token"). Two
 * Mayor-owned artifacts disagreed; the one that is a hard interface wins.
 *
 * The rationale is the product: this app is a PERMANENTLY PINNED TAB. Without
 * persistence, every browser restart greets the user with a Connect button
 * and an empty calendar. The security delta is nil — there is no backend, so
 * no refresh token exists to protect, the access token is read-only
 * (calendar.readonly), it expires in ~1h, and it already lives in the page's
 * memory where any script on the origin could read it.
 */

const TOKEN_AREA = 'calendar';
const TOKEN_KEY = 'token';
const POLL_SECONDS = 300;

export type CalendarStatus =
  | 'unconfigured' // no VITE_GOOGLE_CLIENT_ID
  | 'disconnected' // configured, but no live token
  | 'connecting'
  | 'loading'
  | 'ready'
  | 'error';

export interface UseGoogleCalendarResult {
  readonly status: CalendarStatus;
  /** Always an array, one entry per day in the window, even when empty. */
  readonly days: readonly DayGroup[];
  readonly error: string | null;
  readonly lastUpdated: number | null;
  readonly connect: () => void;
  readonly disconnect: () => void;
  readonly refresh: () => void;
}

function isStoredToken(v: unknown): v is AccessToken {
  if (typeof v !== 'object' || v === null) return false;
  const t = v as Partial<AccessToken>;
  return typeof t.accessToken === 'string' && typeof t.expiresAt === 'number';
}

function readToken(): AccessToken | null {
  const stored = readJson<AccessToken | null>(TOKEN_AREA, TOKEN_KEY, null, (v): v is AccessToken | null =>
    v === null || isStoredToken(v),
  );
  return isTokenLive(stored) ? stored : null;
}

/** Module-level: one authenticated round-trip even under StrictMode. */
let inFlight: Promise<boolean> | null = null;

export function useGoogleCalendar(): UseGoogleCalendarResult {
  // Re-groups at local midnight without a refetch: the three-day window
  // slides, so "Today" is never yesterday on a tab left open overnight.
  const todayKey = useLocalDateKey();

  const [token, setToken] = useState<AccessToken | null>(() =>
    isConfigured.calendar ? readToken() : null,
  );
  const [events, setEvents] = useState<readonly CalendarEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  // Read inside the polling callback without making it a dependency.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const clearToken = useCallback((revoke: boolean) => {
    const current = tokenRef.current;
    if (revoke && current !== null) revokeAccessToken(current.accessToken);
    removeJson(TOKEN_AREA, TOKEN_KEY);
    tokenRef.current = null;
    setToken(null);
    setEvents([]);
    setLastUpdated(null);
  }, []);

  const load = useCallback(async (): Promise<boolean> => {
    const current = tokenRef.current;
    if (!isTokenLive(current)) return false;
    if (inFlight !== null) return inFlight;

    const run = (async (): Promise<boolean> => {
      const outcome = await loadCalendar(current.accessToken);
      if (outcome.ok) {
        setEvents(outcome.value.events);
        setError(null);
        setLastUpdated(Date.now());
        return true;
      }
      // An expired or revoked token is not an error state — it is the
      // disconnected state, and the user's next action is "Connect" again.
      if (outcome.unauthorized) {
        clearToken(false);
        setError(null);
      } else {
        setError(outcome.error);
      }
      return false;
    })().finally(() => {
      inFlight = null;
    });

    inFlight = run;
    return run;
  }, [clearToken]);

  const { refresh: pollRefresh } = usePolling(load, POLL_SECONDS, {
    disabled: token === null,
  });

  const connect = useCallback(() => {
    const clientId = env.calendar.googleClientId;
    if (clientId === undefined || connecting) return;
    setConnecting(true);
    setError(null);
    // The click IS the user gesture GIS needs; nothing may await before this.
    void requestAccessToken(clientId).then((outcome) => {
      setConnecting(false);
      if (!outcome.ok) {
        // A user who closed the popup made a choice — do not shout about it.
        setError(outcome.dismissed ? null : outcome.error);
        return;
      }
      writeJson<AccessToken>(TOKEN_AREA, TOKEN_KEY, outcome.token);
      tokenRef.current = outcome.token;
      setToken(outcome.token);
      // usePolling's effect re-runs when `disabled` flips, which fetches.
    });
  }, [connecting]);

  const disconnect = useCallback(() => {
    clearToken(true);
    setError(null);
  }, [clearToken]);

  const days = useMemo(
    () => groupEventsByDay(events, windowDateKeys()),
    // todayKey is the real dependency: it changes at local midnight and is
    // what makes the window slide. `events` alone would keep yesterday's days.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [events, todayKey],
  );

  // A transient failure over data we already have is NOT an error state: the
  // card keeps the last good day list and `error` is surfaced as a small
  // "stale" pill instead. `error` only takes over when there is nothing to show.
  const status: CalendarStatus = !isConfigured.calendar
    ? 'unconfigured'
    : connecting
      ? 'connecting'
      : token === null
        ? 'disconnected'
        : lastUpdated !== null
          ? 'ready'
          : error !== null
            ? 'error'
            : 'loading';

  return { status, days, error, lastUpdated, connect, disconnect, refresh: pollRefresh };
}
